<template>
  <div
    :id="id"
    class="highlight-item-section"
    :class="`highlight-item-section--${screenOrientationType}`"
  >
    <div
      class="highlight-item-section__race-angle"
      :class="{
        'highlight-item-section__race-angle--current':
          highlightData.highlightId === selectedHighlightId && !selectedPlayerMasterId,
      }"
      @click="changeScene(highlightData)"
    >
      <div class="highlight-item-section__header">
        <p
          v-if="selectedHighlightTab === 'official'"
          class="highlight-item-section__header__title-label"
        >
          <span>{{ playEventName }}</span>
        </p>
        <template v-else>
          <figure class="highlight-item-section__header__user-image">
            <img :src="highlightCreateUser.userPicture" alt="" />
          </figure>
          <p class="highlight-item-section__header__user-name">
            {{ highlightCreateUser.displayName }}
          </p>
        </template>
        <div class="highlight-item-section__header__information">
          <p
            v-if="selectedHighlightTab === 'user'"
            class="highlight-item-section__header__type"
            :class="[`${highlightCreateUser.userType}`]"
          >
            <span class="highlight-item-section__header__type-label">{{
              roleLabels[highlightCreateUser.userType]
            }}</span>
          </p>
          <div class="highlight-item-section__header__time">{{ highlight.raceTime }}</div>
        </div>
      </div>
      <figure
        class="highlight-item-section__thumbnail"
        :class="{ 'highlight-item-section__thumbnail--blank': !highlight.thumbnailUri }"
      >
        <img
          :src="
            highlight.thumbnailUri
              ? highlightThumbnailUri
              : require('@/assets/img/icon/icon_play__gray.svg')
          "
          alt=""
        />
        <template v-if="isPc">
          <p v-if="isInProgress" class="highlight-item-section__thumbnail-status">
            {{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.preparing') }}
          </p>
          <p v-if="isError" class="highlight-item-section__thumbnail-error">
            {{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.error') }}
          </p>
          <button
            v-if="isComplete"
            class="highlight-item-section__thumbnail-button-download"
            @click.stop="handleDownloadClicked"
          >
            <span>{{ $tc('RaceVideoPage.highlights.sns.xAndInstagram.download') }}</span>
            <svg
              width="18"
              height="19"
              viewBox="0 0 18 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M1.75 10.0908V17.6184H16.25V10.0908H14.75V16.1184H3.25V10.0908H1.75Z"
                fill="#00DBA6"
              />
              <path
                d="M11.8297 8.08257L9.74838 10.1639L9.74792 2.09082H8.24792L8.24838 10.1581L6.17282 8.08257H4.0515L8.29414 12.3252C8.68467 12.7157 9.31783 12.7157 9.70836 12.3252L13.951 8.08257H11.8297Z"
                fill="#00DBA6"
              />
            </svg>
          </button>
        </template>
      </figure>
    </div>

    <div
      v-for="(v, i) in highlight.playerMasterIds"
      :key="i"
      class="highlight-item-section__player-angle"
      :class="{
        'highlight-item-section__player-angle--current':
          v === selectedPlayerMasterId && highlightData.id === selectedHighlightId,
      }"
      @click="changeScenePlayer(v)"
    >
      <button class="highlight-item-section__player-angle__button">
        <img
          :src="
            v === selectedPlayerMasterId && highlightData.id === selectedHighlightId
              ? require('@/assets/img/icon/icon_video__black.svg')
              : require('@/assets/img/icon/icon_video__white.svg')
          "
          alt=""
        />
        <span class="highlight-item-section__player-angle__name">{{ playerName(v) }}</span>
      </button>
    </div>

    <div class="highlight-item-section__parent-comment">
      <p
        v-if="!enabledEditHighlight"
        class="highlight-item-section__parent-comment__comment"
        v-html="highlightComment.comment"
      />

      <div
        v-if="!enabledEditHighlight"
        class="highlight-item-section__parent-comment__footer"
        :class="{
          'highlight-item-section__parent-comment__hidden-like-button': !highlightData.isShared,
        }"
      >
        <like-button-parts
          v-if="highlightData.isShared"
          :commentData="highlightComment"
          @click="onChangeLike"
        />
        <div>
          <comment-post-date-parts :postDate="highlightComment.date" />
          <button
            v-if="highlight._organization !== parentOrgId"
            class="highlight-item-section__parent-comment__controller-button"
            @click="handleOpenMenuClicked(highlightComment)"
          >
            <svg
              width="16"
              height="14"
              viewBox="0 0 16 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M2.5 6.05C3.13 6.05 3.65 6.57 3.65 7.2C3.65 7.84 3.13 8.36 2.5 8.36C1.86 8.36 1.34 7.84 1.34 7.2C1.34 6.57 1.86 6.05 2.5 6.05ZM7.9 6.05C8.53 6.05 9.05 6.57 9.05 7.2C9.05 7.84 8.53 8.36 7.9 8.36C7.26 8.36 6.74 7.84 6.74 7.2C6.74 6.57 7.26 6.05 7.9 6.05ZM13.3 6.05C13.93 6.05 14.45 6.57 14.45 7.2C14.45 7.84 13.93 8.36 13.3 8.36C12.66 8.36 12.14 7.84 12.14 7.2C12.14 6.57 12.66 6.05 13.3 6.05Z"
                fill="white"
              />
            </svg>
          </button>
        </div>
      </div>

      <comment-editor-parts
        v-if="enabledEditHighlight"
        class="highlight-item-section__parent-comment__editor"
        :screenOrientationType="screenOrientationType"
        :message="highlightComment.comment"
        @handlerCancel="enabledConfirmModal = true"
        @handlerSave="onSaveHighlightComment"
      />
    </div>
    <div v-if="highlightCommentCount > 0" class="highlight-item-section__posted-comments">
      <div v-if="visibleCommentList" class="highlight-item-section__posted-comments__list">
        <!-- デフォルトで表示されるコメント-->
        <highlight-comment-section
          v-for="(commentData, idx) in highlightComments[0]"
          :id="`${id}Comment${idx}`"
          :screenOrientationType="screenOrientationType"
          :key="`HighlightCommentSection${idx}`"
          :commentData="commentData"
          :loginUserId="loginUserId"
          :showCommentInput="showCommentInput"
          :targetIsEditMode="editModesMap[commentData.commentId]"
          :retrieveNameUsersByUserId="retrieveNameUsersByUserId"
          @handlerEdit="handlerEdit"
          @onShowDeleteConfirmModal="onShowDeleteConfirmModal"
          @onToggleIsEditMode="
            onToggleIsEditMode($event, commentData.commentId, `${id}Comment${idx}`)
          "
          @onChangeLike="onChangeLike"
          @onClickMenu="handleCommentMenuClicked"
        />

        <atom-button
          v-if="displayMoreCommentsButton"
          class="highlight-item-section__more-button"
          :linkText="$tc('RaceVideoPage.highlights.comment.more')"
          @onClick="showMoreComment"
        />

        <template v-if="isShowAllComment || displayNewOwnHighlightComments.length > 0">
          <!-- 「もっとコメントを見る」を押した時 または コメントが5件以上ある状態で新たにコメントを作成した時に表示されるコメント-->
          <highlight-comment-section
            v-for="(commentData, idx) in displayMoreComments"
            :id="`${id}Comment${idx}`"
            :screenOrientationType="screenOrientationType"
            :key="`HighlightCommentSection${idx}_more`"
            :commentData="commentData"
            :loginUserId="loginUserId"
            :showCommentInput="showCommentInput"
            :targetIsEditMode="editModesMap[commentData.commentId]"
            :retrieveNameUsersByUserId="retrieveNameUsersByUserId"
            @handlerEdit="handlerEdit"
            @onShowDeleteConfirmModal="onShowDeleteConfirmModal"
            @onToggleIsEditMode="
              onToggleIsEditMode($event, commentData.commentId, `${id}Comment${idx}`)
            "
            @onChangeLike="onChangeLike"
            @onClickMenu="handleCommentMenuClicked"
          />
        </template>
      </div>

      <highlight-comment-toggle-parts
        :isActive="visibleCommentList"
        :commentCounts="highlightCommentCount"
        @onClickToggle="toggleComments"
      />
    </div>

    <div
      v-if="showCommentInput"
      :id="`${id}PostInput`"
      class="highlight-item-section__input-comment"
    >
      <comment-input-section
        :targetEnableInput="targetEnableInput"
        :targetInputHighlightComment="targetInputHighlightComment"
        @onToggleEnabledInput="onToggleEnabledInput($event, highlightData.highlightId || '')"
        @onChangeHighlightComment="
          onChangeHighlightComment($event, highlightData.highlightId || '')
        "
        @handlerSubmit="handlerSubmit(highlightData)"
      />
    </div>

    <!-- 入力キャンセル確認モーダル -->
    <confirm-modal-section
      v-if="enabledConfirmModal"
      class="highlight-item-section__modal"
      :message="$tc('RaceVideoPage.highlights.comment.inputCancelModal.editCancelMessage')"
      :confirmButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.confirmButtonLabel')
      "
      :cancelButtonLabel="
        $tc('RaceVideoPage.highlights.comment.inputCancelModal.cancelButtonLabel')
      "
      @confirm="onCancelHighlightCommentInput"
      @cancel="enabledConfirmModal = false"
    />
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  ComputedRef,
  onMounted,
  PropType,
  ref,
  Ref,
  watch,
} from '@vue/composition-api'
import { orderBy } from 'lodash'
import HighlightDocument from '@/store/stores/collectionModule/documents/highlight/HighlightDocument'
import PlayEventDocument from '@/store/stores/collectionModule/documents/highlight/PlayEventDocument'
import I18n from '@/locales/I18n'
import PlayerDocument from '@/store/stores/collectionModule/documents/player/PlayerDocument'
import CloudFrontUtil from '@/util/aws/CloudFrontUtil'
import HighlightCommentSection from '@/components/RaceVideoPage/RaceVideoHighlightsPane/HighlightCommentSection.vue'
import LikeButtonParts, {
  ClickedLikeDataType,
} from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/LikeButtonParts.vue'
import CommentPostDateParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/CommentPostDateParts.vue'
import CommentInputSection from '@/components/RaceVideoPage/RaceVideoHighlightsPane/CommentInputSection.vue'
import CommentEditorParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/CommentEditorParts.vue'
import useCommunication, {
  CommentDataType,
  ParentCommentDataType,
} from '@/components/RaceVideoPage/hook/useCommunication'
import usePermission from '@/components/hook/usePermission'
import useUserTypeRoles from '@/components/hook/useUserTypeRoles'
import Const from '@/util/Const'
import { UserRetrieveNameResponseType } from '@/store/stores/collectionModule/documents/user/UserRetrieveNameDocument'
import ConfirmModalSection from '@/components/common/modal/ConfirmModalSection.vue'
import HighlightCommentToggleParts from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/HighlightCommentToggleParts.vue'
import AtomButton from '@/components/atoms/AtomButton.vue'
import { HighlightTabsType } from '@/store/stores/pageStore/RaceVideoPage/RaceVideoPageStore'
import StoreUtil from '@/store/StoreUtil'
import { UserType } from '@/store/stores/pageStore/common/UserType'
import SceneMoviesDocument from '@/store/stores/collectionModule/documents/highlight/SceneMoviesDocument'
import DeviceInfo from '@/util/DeviceInfo'
import LoginStore from '@/store/stores/loginStore/LoginStore'
import { HighlightSlideMenuType } from '@/components/RaceVideoPage/RaceVideoHighlightsPane/parts/HighlightsSlideMenuParts.vue'

/* eslint-disable @typescript-eslint/no-var-requires */
const defaultIcon = require('@/assets/img/icon/icon_user_default.png')

type HighlightCreateUserType = {
  displayName: string
  userPicture: string
  userType: UserType
}

/**
 * レース動画再生画面 ハイライトアイテム セクションコンポーネント
 */
export default defineComponent({
  name: 'HighlightItemSection',
  components: {
    AtomButton,
    HighlightCommentToggleParts,
    ConfirmModalSection,
    HighlightCommentSection,
    LikeButtonParts,
    CommentPostDateParts,
    CommentInputSection,
    CommentEditorParts,
  },
  props: {
    /**
     * 画面向き
     */
    screenOrientationType: {
      type: String,
      default: 'portrait-primary',
    },
    id: {
      type: String,
    },
    current: {
      type: String,
    },
    currentPlayer: {
      type: String,
    },
    /**
     * 選択中のハイライトタブ
     */
    selectedHighlightTab: {
      type: String as PropType<HighlightTabsType>,
      required: true,
    },
    highlightData: {
      type: Object as PropType<HighlightDocument>,
      required: true,
    },
    playEventList: {
      type: Array as PropType<Array<PlayEventDocument>>,
      default: () => [],
    },
    playerList: {
      type: Array as PropType<Array<PlayerDocument>>,
      default: () => [],
    },
    targetHighlightComments: {
      type: Array as PropType<Array<CommentDataType>>,
      default: () => [],
    },
    loginUserId: {
      type: String,
      required: true,
    },
    /**
     * 対象ハイライトのコメント入力状態
     */
    targetEnableInput: {
      type: Boolean,
      default: false,
    },
    /**
     * 対象ハイライトに対する入力コメント
     */
    targetInputHighlightComment: {
      type: String,
      default: '',
    },
    /**
     * ハイライトのコメント編集状態（コメント毎に入力状態を保持する）
     */
    editModesMap: {
      type: Object as PropType<Record<string, boolean>>,
      default: () => ({}),
    },
    /**
     * ハイライトのメインコメント編集状態（ハイライト毎に入力状態を保持する）
     */
    editModesMainCommentMap: {
      type: Object as PropType<Record<string, boolean>>,
      default: () => ({}),
    },
    /**
     * ハイライトコメントを入力するために動画再生画面に遷移してきたか
     */
    inputComment: {
      type: String,
      default: '',
    },
    /**
     * ハイライトコメントしたユーザーのマップ
     */
    retrieveNameUsersByUserId: {
      type: Object as PropType<Record<string, UserRetrieveNameResponseType>>,
    },
    /**
     * ハイライトID
     * 遷移先の画面から戻ってきた時に対象のハイライトにスクロールさせるために利用する
     */
    scrollToHighlightId: {
      type: String,
      default: '',
    },
    /**
     * 動画集
     */
    sceneMovies: {
      type: Object as PropType<SceneMoviesDocument | null>,
      default: () => ({}),
    },
    /**
     * ハイライトコメントの数
     */
    highlightCommentCount: {
      type: Number,
      default: 0,
    },
  },
  setup(props, context) {
    const isPc = DeviceInfo.isForPC()
    const raceVideoPageStore = StoreUtil.useStore('RaceVideoPageStore')
    const { loginId } = LoginStore.value
    const { hasPermission } = usePermission()
    const { createDisplayHighlightCommentData } = useCommunication()
    const { getUserTypeRoles } = useUserTypeRoles()

    /** 全コメントを表示するかどうか */
    const isShowAllComment = ref(false)
    /** コメントリストを表示するかどうか */
    const visibleCommentList = ref(false)

    const highlight = computed(() => props.highlightData)
    const selectedHighlightId = computed(() => props.current)
    const selectedPlayerMasterId = computed(() => props.currentPlayer)
    const playEvents = computed(() => props.playEventList)
    const players = computed(() => props.playerList)
    const enabledConfirmModal = ref<boolean>(false)

    // ハイライト動画切り抜き：ダウンロード準備中かどうか
    const isInProgress = computed(() => props.sceneMovies?.status === 'InProgress')
    // ハイライト動画切り抜き：ダウンロード準備が完了しているかどうか
    const isComplete = computed(() => props.sceneMovies?.status === 'Complete')
    // ハイライト動画切り抜き：エラーかどうか
    const isError = computed(() => props.sceneMovies?.status === 'Error')

    /**
     * シーン名を取得する
     */
    const sceneName = computed(() => {
      const sceneData = playEvents.value.find(
        (scene: PlayEventDocument) => scene.playEventId === highlight.value.playEventId,
      )
      if (sceneData) {
        return sceneData.playEventName[I18n.locale]
      }
      return ''
    })

    /**
     * ハイライトのサムネイル画像のURL
     */
    const highlightThumbnailUri = computed(() =>
      CloudFrontUtil.getSignedUrl(highlight.value.thumbnailUri),
    )

    /**
     * ハイライトコメント
     */
    const highlightComment = computed(() => createDisplayHighlightCommentData(highlight.value))

    /**
     * コメントデータ
     * 初期表示するコメントともっとコメントを見るを押した時に表示するコメントを分ける
     * @return [displayedComments（初期表示されているコメント）, moreComments（もっとコメントを見るを押した時に表示されるコメント）]
     */
    const highlightComments = computed((): CommentDataType[][] => {
      // 初期表示されているコメント
      const displayedComments: CommentDataType[] = []
      // もっとコメントを見るを押した時に表示されるコメント
      const moreComments: CommentDataType[] = []

      // 新規で登録したコメントが上に表示されてしまったため、日付が過去順になるように並べ替え直す
      orderBy(props.targetHighlightComments, '_createdDate').forEach((comment, index) => {
        if (index < 5) {
          displayedComments.push(comment)
        } else {
          moreComments.push(comment)
        }
      })
      return [displayedComments, moreComments]
    })

    /**
     * もっとコメントを見るの下に表示するコメント
     */
    const displayNewOwnHighlightComments = computed(() =>
      raceVideoPageStore.newOwnHighlightComments.value.filter((comment) => {
        if (props.highlightData.highlightId !== comment.highlightId) {
          // 対象ハイライトカードのコメントではない場合は表示しない
          return false
        }

        const isDisplayed = highlightComments.value[0].some(
          (displayedComment) => displayedComment.commentId === comment.commentId,
        )
        // コメント数が5件以下ですでに表示されている場合はもっとコメントを見るの下に表示しない
        return !isDisplayed
      }),
    )

    /**
     * もっとコメントを見るを表示するかどうか
     */
    const displayMoreCommentsButton = computed(() => {
      if (isShowAllComment.value) {
        // もっとコメントを見るを押した場合、「もっとコメントを見る」ボタンを表示しない
        return false
      }
      if (highlightComments.value[1].length === 0) {
        // コメント5件以下の場合、「もっとコメントを見る」ボタンを表示しない
        return false
      }
      if (
        displayNewOwnHighlightComments.value.length > 0 &&
        displayNewOwnHighlightComments.value.length === highlightComments.value[1].length
      ) {
        // コメント5件の状態でコメントを投稿した場合、自分のコメントが表示されるため、「もっとコメントを見る」ボタンを表示しない
        return false
      }

      // 上記以外
      return true
    })

    /**
     * 全コメント
     * もっとコメントを見るを押した時 または コメントが5件以上ある状態で新たに作成した自分のコメントを表示する
     */
    const displayMoreComments: ComputedRef<Array<CommentDataType>> = computed(() =>
      isShowAllComment.value ? highlightComments.value[1] : displayNewOwnHighlightComments.value,
    )

    /**
     * コメントの入力が可能かどうか
     * - ハイライトにコメントできる権限を持つユーザーかつハイライトが共有されているもしくは公式ハイライトならコメントができる
     */
    const showCommentInput = computed(
      () => hasPermission('createComment') && props.highlightData.isShared,
    )

    onMounted(() => {
      setTimeout(() => {
        if (
          props.inputComment === 'true' &&
          props.highlightData.highlightId === props.scrollToHighlightId
        ) {
          // 対象のハイライトコメントにスクロールする
          context.emit('onScrollToComment', `${props.id}PostInput`)
        }

        if (props.highlightData.highlightId === props.scrollToHighlightId) {
          // 対象のハイライトにスクロールする
          context.emit('onScrollToHighlight', `highlightItemSection${props.scrollToHighlightId}`)
        }
      }, 100)
    })

    /** シェア、編集、削除ボタンを表示するかどうか */
    const controllerShown = ref<boolean>(false)

    /** コメントシェア関数取得 */
    const { shareHighlightComment } = useCommunication()

    /** ハイライト メインコメント編集モード */
    const enabledEditHighlight = computed(() => {
      if (
        props.highlightData.highlightId !== null &&
        props.editModesMainCommentMap[props.highlightData.highlightId]
      ) {
        return props.editModesMainCommentMap[props.highlightData.highlightId]
      }

      return false
    })

    const handleOpenMenuClicked = (comment: ParentCommentDataType) => {
      // 自分のハイライトの場合は、編集、削除などをできるようにする
      let slideMenu = ''
      if (loginId === comment.userId) {
        slideMenu = isPc ? 'highlightPc' : 'highlight'
      } else {
        slideMenu = 'userBlock'
      }
      context.emit('onClickOpenMenu', comment, slideMenu)
    }

    /**
     * 投稿されたコメントを開閉する
     */
    const toggleComments = () => {
      visibleCommentList.value = !visibleCommentList.value

      if (!visibleCommentList.value) {
        // コメントを非表示にした場合の処理
        // コメントを見ていたハイライトの位置までスクロールさせる
        context.emit('onScrollToHighlight', `highlightItemSection${props.highlightData.id}`)
        // コメント全件表示をやめる
        isShowAllComment.value = false
        // もっとコメントを見るの下に表示された自分の新規作成コメントをクリア
        raceVideoPageStore.newOwnHighlightComments.value = []
      } else {
        context.emit(
          'fetchDiffTargetHighlightCommentsAndReactions',
          props.highlightData.highlightId,
        )
      }
    }

    /**
     * 「もっとコメントを見る」が押下されたときの処理
     */
    const showMoreComment = () => {
      isShowAllComment.value = true

      // もっとコメントを見るの下に表示された自分の新規作成コメントをクリア
      raceVideoPageStore.newOwnHighlightComments.value = []
    }

    /**
     * ハイライトを作成したユーザーの情報を返す
     */
    const highlightCreateUser: Ref<HighlightCreateUserType | null> = computed(() => {
      if (!props.highlightData._createdBy) {
        return null
      }
      const targetUser = props.retrieveNameUsersByUserId?.[props.highlightData._createdBy]

      const userTypeRoles = getUserTypeRoles(targetUser?.roles)

      const userRole = userTypeRoles?.find((role) => Const.SFGO_ROLES_FOR_USER_TYPE.includes(role))

      const contractPlan = targetUser?.contract?.planGroupId?.includes('freePlan')
        ? 'free-user'
        : 'paid-user'

      return {
        displayName: targetUser?.additionalData?.userDisplayName || '---',
        userPicture: targetUser?.userPicture
          ? CloudFrontUtil.getSignedUrl(targetUser.userPicture)
          : defaultIcon,
        userType: userRole ?? contractPlan,
      }
    })

    /**
     * コメントのメニューボタンが押下されたときの処理
     */
    const handleCommentMenuClicked = (
      commentData: CommentDataType,
      slideMenu: HighlightSlideMenuType,
    ) => {
      context.emit('onClickCommentMenu', commentData, slideMenu)
    }

    /**
     * 新たに自分が投稿したコメントを監視する
     */
    watch(
      () => raceVideoPageStore.newOwnHighlightComments.value,
      (newCommentList) => {
        if (
          !visibleCommentList.value &&
          newCommentList.length > 0 &&
          newCommentList[newCommentList.length - 1].highlightId === props.highlightData.highlightId
        ) {
          // コメントを投稿した場合、投稿対象のハイライトコメントを開いた状態にし、投稿したコメントを確認できるようにする
          visibleCommentList.value = true
        }
      },
    )

    return {
      isShowAllComment,
      selectedHighlightId,
      selectedPlayerMasterId,
      playEventName: sceneName,
      players,
      highlight: highlight as ComputedRef<HighlightDocument>,
      highlightThumbnailUri,
      displayMoreCommentsButton,
      highlightComments,
      controllerShown,
      showCommentInput,
      highlightComment,
      shareHighlightComment,
      enabledEditHighlight,
      enabledConfirmModal,
      handleOpenMenuClicked,
      parentOrgId: process.env.VUE_APP_SFGO_PARENT_ORG_ID,
      toggleComments,
      showMoreComment,
      displayMoreComments,
      visibleCommentList,
      displayNewOwnHighlightComments,
      highlightCreateUser: highlightCreateUser as Ref<HighlightCreateUserType>,
      handleCommentMenuClicked,
      roleLabels: Const.SFGO_USER_TYPE_LABELS,
      isInProgress,
      isComplete,
      isError,
      isPc,
    }
  },
  methods: {
    /**
     * ハイライトカードが選択された際に呼び出される。
     * ハイライトシーン選択切り替えをを行う。
     */
    changeScene(highlight: HighlightDocument) {
      if (highlight.isCreatedBySfgoUser && highlight.playerMasterIds) {
        this.$emit(
          'sceneChange',
          highlight.id,
          highlight.isCreatedBySfgoUser,
          highlight.playerMasterIds[0],
        )
      } else {
        this.$emit('sceneChange', highlight.id, highlight.isCreatedBySfgoUser)
      }
    },

    /**
     * ハイライトカードの選手が選択された場合に呼び出される。
     * ハイライトシーンオンボード選択切り替えを行う。
     * @param playerMasterId 選択された選手の選手マスタID
     */
    changeScenePlayer(playerMasterId: string) {
      // 選手と、ハイライトカードを選択状態にする
      this.$emit(
        'sceneChange',
        this.highlightData.highlightId,
        this.highlightData.isCreatedBySfgoUser,
        playerMasterId,
      )
    },

    /**
     * ハイライトに関連づけられている選手の名前を取得する。
     * @param playerMasterId 選手マスタID
     */
    playerName(playerMasterId: string) {
      const targetPlayer = this.players.find(
        (player: PlayerDocument) => player.playerMasterId === playerMasterId,
      )
      if (targetPlayer) {
        return targetPlayer.playerName[this.$i18n.locale] || ''
      }
      return ''
    },
    /**
     * コメント編集ボタン押下時の処理
     * @param editedComment 編集後のコメント
     * @param commentId 編集対象のコメントID
     */
    handlerEdit(editedComment: string, commentId: string) {
      this.$emit('handlerEdit', editedComment, commentId)
    },
    /**
     * コメント投稿ボタン押下時の処理
     * @param highlight コメント対象のハイライト
     */
    handlerSubmit(highlight: HighlightDocument) {
      this.$emit('handlerSubmit', highlight)
    },
    /**
     * コメント削除確認モーダル表示通知
     * @param commentId コメントID
     */
    onShowDeleteConfirmModal(commentId: string) {
      this.$emit('onShowDeleteConfirmModal', commentId)
    },
    /**
     * コメント投稿入力開始/終了通知
     * @param enabledInput ハイライトの入力状態
     * @param highlightId コメント対象のハイライトID
     */
    onToggleEnabledInput(enabledInput: boolean, highlightId: string) {
      if (enabledInput) {
        // コメント入力可能な場合は、入力位置までスクロールするように親に伝える
        this.$emit('onScrollToComment', `${this.id}PostInput`)
      }

      this.$emit('onToggleEnabledInput', enabledInput, highlightId)
    },
    /**
     * コメント編集開始/終了通知
     * @param isEditMode ハイライトコメント編集状態
     * @param commentId 編集対象のコメントID
     * @param scrollToId スクロール対象のコメントID
     */
    onToggleIsEditMode(isEditMode: boolean, commentId: string, scrollToId: string) {
      if (isEditMode) {
        // コメント入力可能な場合は、入力位置までスクロールするように親に伝える
        this.$emit('onScrollToComment', scrollToId)
      }

      this.$emit('onToggleIsEditMode', isEditMode, commentId)
    },
    /**
     * 入力コメント変更
     * @param value 入力したハイライトコメント
     * @param highlightId コメント対象のハイライトID
     */
    onChangeHighlightComment(value: string, highlightId: string) {
      this.$emit('onChangeHighlightComment', value, highlightId)
    },
    /**
     * コメントLIKE処理
     * @param likeData 押下されたいいねの情報
     */
    onChangeLike(likeData: ClickedLikeDataType) {
      this.$emit('onChangeLike', likeData)
    },
    /**
     * 親コメント編集中止
     */
    onCancelHighlightCommentInput() {
      this.enabledConfirmModal = false
      this.$emit('onToggleMainCommentIsEditMode', false, this.highlightData.highlightId)
    },
    /**
     * 親コメント編集保存
     * @param comment 編集後のコメント
     */
    onSaveHighlightComment(comment: string) {
      this.$emit('onChangeHighlightMainComment', comment, this.highlightData.highlightId)
    },
    /**
     * ダウンロード可能状態でユーザーがダウンロードボタンを押した際にファイルをダウンロードする
     */
    handleDownloadClicked() {
      this.$emit('downloadHighlight', this.sceneMovies)
    },
  },
})
</script>

<style lang="scss" scoped>
.highlight-item-section {
  position: relative;
  width: min(100%, 328px);
  margin-bottom: 16px;
  background: $black80;
  border-radius: 8px;

  &::before {
    position: absolute;
    left: calc(50% - 1px);
    top: 0;
    transform: translateY(-100%);
    display: block;
    content: '';
    width: 2px;
    height: 16px;
    background: rgba(#e5e5e5, 50%);
  }

  &:first-child {
    &::before {
      height: 18px;
    }
  }

  &__header {
    width: 100%;
    padding: 8px;
    font-size: 13px;
    color: $white;
    line-height: 1.8;
    background: $black20;
    border-radius: 4px 4px 0 0;

    &::after {
      display: table;
      clear: both;
      content: '';
    }

    &__icon {
      width: 18px;
      margin-right: 8px;
    }

    &__title {
      &-label {
        display: inline;
        padding-top: 4px;
        font-size: 13px;
        font-weight: 600;
        line-height: 1.38;
        font-feature-settings: 'dnom' on, 'kern' off;
      }
    }

    &__user {
      display: flex;
      gap: 8px;
      align-items: center;

      &-image {
        width: 24px;
        height: 24px;
        overflow: hidden;
        margin-right: 8px;
        border-radius: 50%;
        float: left;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      &-name {
        display: inline;
        font-size: 13px;
        font-weight: 500;
        color: $white;
        letter-spacing: 0.04em;
        line-height: 1.53;
      }
    }

    &__information {
      display: flex;
      gap: 4px;
      float: right;
    }

    &__type {
      position: relative;

      &-label {
        position: relative;
        z-index: 2;
        display: flex;
        align-items: center;
        justify-content: center;
        height: 24px;
        padding: 0 8px 0 12px;
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.04em;
        text-transform: capitalize;
        border-width: 1px;
        border-style: solid;
        border-radius: 2px;
      }

      &::before {
        position: absolute;
        top: 1px;
        left: 1px;
        z-index: 3;
        width: 4px;
        height: 22px;
        content: '';
      }

      &::after {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        width: 100%;
        height: 100%;
        content: '';
        background-color: rgba(#000, 0.5);
        border-radius: 2px;
      }

      &.driver {
        .highlight-item-section__header__type-label {
          color: #20cabd;
          background-color: rgba(#20cabd, 0.2);
          border-color: #20cabd;
        }

        &::before {
          background-color: #20cabd;
        }
      }

      &.team-user,
      &.team-engineer {
        .highlight-item-section__header__type-label {
          color: #53e2e2;
          background-color: rgba(#53e2e2, 0.2);
          border-color: #53e2e2;
        }

        &::before {
          background-color: #53e2e2;
        }
      }

      &.paid-user {
        .highlight-item-section__header__type-label {
          color: #cbaeff;
          background-color: rgba(#cbaeff, 0.2);
          border-color: #cbaeff;
        }

        &::before {
          background-color: #cbaeff;
        }
      }

      &.free-user {
        .highlight-item-section__header__type-label {
          color: #e2e0ff;
          background-color: rgba(#e2e0ff, 0.2);
          border-color: #e2e0ff;
        }

        &::before {
          background-color: #e2e0ff;
        }
      }

      &.jrp-staff {
        .highlight-item-section__header__type-label {
          padding-left: 8px;
          color: #4fafff;
          background-color: rgba(#4fafff, 0.2);
          border-color: #4fafff;
        }

        &::before {
          display: none;
        }
      }

      &.guest {
        .highlight-item-section__header__type-label {
          color: #ffbb00;
          background-color: rgba(#ffbb00, 0.2);
          border-color: #ffbb00;
        }

        &::before {
          background-color: #ffbb00;
        }
      }

      &.ambassador,
      &.adviser,
      &.special-supporter {
        .highlight-item-section__header__type-label {
          color: #ff4949;
          background-color: rgba(#ff4949, 0.2);
          border-color: #ff4949;
          white-space: pre;
          line-height: 10px;
        }

        &::before {
          background-color: #ff4949;
        }
      }

      &.special {
        width: 83px;
        line-height: 0.9;

        .highlight-item-section__header__type-label {
          color: #ff4949;
          background-color: rgba(#ff4949, 0.2);
          border-color: #ff4949;
        }

        &::before {
          background-color: #ff4949;
        }
      }
    }

    &__time {
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 76px;
      height: 24px;
      white-space: nowrap;
      text-align: center;
      font-size: 12px;
      line-height: 1.33;
      letter-spacing: 0.04em;
      border-radius: 2px;
      background: rgba(0, 0, 0, 70%);
    }
  }

  &__thumbnail {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    background-color: $black90;

    img {
      width: 100%;
      aspect-ratio: 65 / 24;
      object-fit: cover;
    }

    &--blank {
      height: 64px;

      img {
        width: 32px;
        aspect-ratio: unset;
        object-fit: unset;
      }
    }

    &-status {
      position: absolute;
      right: 8px;
      bottom: 8px;
      padding: 4px 12px;
      font-size: 12px;
      font-weight: 500;
      color: $white70;
      letter-spacing: 0.02em;
      background-color: $black70;
      border-radius: 32px;
    }

    &-error {
      position: absolute;
      right: 8px;
      bottom: 8px;
      max-width: 300px;
      padding: 4px 12px;
      font-size: 12px;
      font-weight: 500;
      color: $white70;
      text-align: center;
      letter-spacing: 0.02em;
      white-space: pre-wrap;
      background-color: $black70;
      border: 1px solid #ff4949;
      border-radius: 32px;
    }

    &-button {
      &-download {
        position: absolute;
        right: 8px;
        bottom: 8px;
        display: flex;
        gap: 4px;
        align-items: center;
        justify-content: center;
        height: 32px;
        padding: 0 25px;
        font-size: 12px;
        font-weight: 500;
        color: $green-light;
        line-height: 1.45;
        letter-spacing: 0.02em;
        background-color: $black;
        border: 1px solid rgba($green-light, 0.5);
        border-radius: 4px;
      }
    }
  }

  &__race-angle {
    cursor: pointer;

    &:last-child .highlight-item-section__thumbnail {
      border-radius: 0 0 4px 4px;
      overflow: hidden;
    }

    &--current {
      .highlight-item-section {
        &__header {
          background: $green-light;

          &__title {
            &-label {
              color: $black;
            }
          }

          &__user {
            &-name {
              color: $black;
            }
          }

          &__type {
            &::before {
              border-top-left-radius: 1px;
              border-bottom-left-radius: 1px;
            }

            &::after {
              background-color: rgba(#000, 0.8);
            }

            &.driver,
            &.team,
            &.paid-user,
            &.free-user,
            &.staff,
            &.guest,
            &.ambassador,
            &.special {
              .highlight-item-section__header__type-label {
                background-color: transparent;
                border-color: transparent;
              }
            }
          }
        }
      }
    }
  }

  &__player-angle {
    justify-content: flex-start;
    width: 100%;

    // TODO: ハイライト機能をリリースするタイミングで確認
    // ハイライトコメント入力欄を考慮した実装だと思われるため、機能を公開する際にコメントを解除して確認する
    // &:last-child {
    //   padding: 3px 0 2px;
    //   border-top: 1px solid $black;
    // }

    &:nth-child(2) {
      padding: 4px 0 2px;
      border-top: none;
    }

    &--current {
      .highlight-item-section__player-angle__button {
        background: $green-light;
        color: $black20;
      }
    }

    &__button {
      display: flex;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      padding: 8px 16px;
      color: $white;
      border-radius: 4px;
    }

    &__name {
      margin-left: 8px;
      font-size: 13px;
      font-weight: 600;
      line-height: 1.38;
      font-feature-settings: 'dnom' on, 'kern' off;
    }
  }

  &__parent-comment {
    position: relative;
    width: 100%;
    padding: 10px 16px;
    border-top: 1px solid $black;

    &__comment {
      font-size: 12px;
      font-weight: 500;
      line-height: 1.67;
      letter-spacing: 0.02em;
      color: $white;
      white-space: pre-wrap;
      overflow-wrap: break-word;
    }

    &__editor {
      width: 100%;
      padding: 10px 10px 0;
      border-radius: 4px;
      background: $black;
      border: 1px solid $black90;
    }

    &__controller {
      top: auto;
      right: 0;
      bottom: 38px;

      &-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 14px;
      }
    }

    &__footer {
      display: flex;
      flex-flow: row wrap;
      align-items: center;
      justify-content: space-between;
      margin-top: 6px;

      > div {
        display: inline-flex;
        flex-flow: row nowrap;
        align-items: center;
        justify-content: flex-end;
        gap: 10px;
      }
    }

    &__hidden-like-button {
      justify-content: flex-end !important;
    }
  }

  &__posted-comments {
    display: flex;
    flex-flow: column nowrap;
    gap: 6px;
    width: 100%;
    padding: 12px 10px 10px;
    border-top: 1px solid $black;

    &__title {
      width: 100%;
      padding-right: 6px;
      font-family: $fontSignup;
      font-weight: 500;
      font-size: 10px;
      line-height: 1.4;
      text-align: right;
      letter-spacing: 0.04em;
      color: $white;
    }

    &__list {
      display: flex;
      flex-flow: column nowrap;
      gap: 8px;
      padding-left: 6px;
    }
  }

  &__more {
    &-button {
      margin-top: 4px;
      background-color: transparent;
      border-color: transparent;
    }
  }

  &__input-comment {
    width: 100%;
    padding: 10px;
    border-top: 1px solid $black;
  }

  &--landscape-primary,
  &--landscape-secondary {
    .highlight-item-section {
      &__posted-comments {
        &__list {
          padding-left: 0;
        }
      }

      &__modal {
        left: auto;
        width: 230px;
      }
    }
  }
}
</style>

<style lang="scss">
.race-video-highlights-pane {
  &--landscape-primary,
  &--landscape-secondary {
    .highlight-item-section {
      margin-bottom: 8px;

      &::before {
        height: 8px;
      }
    }
  }
}
</style>
